/**
 * @typedef {import('./strategyTypes').PersistentStorage} PersistentStorage
 * @typedef {import('./strategyTypes').PersistentStorageStrategy} PersistentStorageStrategy
 */

import { Cookies } from 'react-cookie';

/* this can be used in the callback of Object.keys or Object.entries */
const cookieBeginsWithPrefix = cookiePrefix => cookieName => {
  if (typeof cookieName === 'string')
    return cookieName.startsWith(cookiePrefix);
  return cookieName[0].startsWith(cookiePrefix);
};

/**
 * @param {string} cookiePrefix
 * @returns PersistentStorage
 */
const cookieStorageFactory = cookiePrefix => {
  const cookies = new Cookies();
  function clear() {
    Object.keys(cookies.getAll())
      .filter(cookieBeginsWithPrefix(cookiePrefix))
      .forEach(cookieName => {
        cookies.remove(cookieName);
      });
  }

  function getItem(keyName) {
    return cookies.getAll()[`${cookiePrefix}${keyName}`] || null;
  }

  function getAllItems() {
    return Object.entries(cookies.getAll())
      .filter(cookieBeginsWithPrefix(cookiePrefix))
      .reduce((acc, [keyName, keyValue]) => {
        acc[keyName.replace(cookiePrefix, '')] = keyValue;
        return acc;
      }, {});
  }

  function removeItem(keyName) {
    cookies.remove(`${cookiePrefix}${keyName}`);
  }

  function setItem(keyName, keyValue) {
    cookies.set(`${cookiePrefix}${keyName}`, keyValue);
  }

  function length() {
    return Object.keys(cookies.getAll()).filter(
      cookieBeginsWithPrefix(cookiePrefix)
    ).length;
  }

  return {
    getItem,
    getAllItems,
    setItem,
    removeItem,
    clear,
    get length() {
      return length();
    }
  };
};

/**
 * @param {string} cookiePrefix
 * @returns PersistentStorageStrategy
 */
export const cookieStorageStrategyFactory = cookiePrefix => {
  /**
   * This is required as we need to return a reference to the created
   * cookie storage implementation instead of rebuilding it each time
   * we go to use the storage instance
   * */
  const cookieStorage = cookieStorageFactory(cookiePrefix);
  return {
    name: 'cookieStorageStrategy',
    isSupported: () => {
      if (
        typeof document === 'undefined' ||
        typeof document.cookie === 'undefined'
      )
        return false;

      try {
        const testCookie = `${cookiePrefix}_test_key=test`;

        document.cookie = testCookie;
        if (!document.cookie.includes(testCookie)) return false;
        document.cookie = `${testCookie}; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
        if (document.cookie.includes(testCookie)) return false;

        return true;
      } catch (e) {
        return false;
      }
    },
    storageInstance: () => cookieStorage
  };
};
